// ThreeJs/Segments/SegmentCopyPaste.js
import * as THREE from "three";

import { POLYGONS, VARS, VERTICES, DrawingGlobals } from "../Global/variables";
import { sceneManager } from "../SetUps/SceneManager";

import { createEdges, createVertex } from "./Drawing";
import { getGeoCenter, earClipping } from "../Global/functions";
import { createNameLabel, indexToLabel } from "./Labels";
import * as Details from "./AreaPitchAzimu";
import { selectSegment } from "./Functions";

// Guides
import { hideSmartGuides } from "./Snaps/GuidingLines";
import { LINES } from "../Global/materials";
// Undo + delete + drag controls
import { pushUndoAction } from "../Global/UndoManager";
import { deleteSingleSegment } from "./Delete";
import { removeSegmentDragControls } from "./DragControls";


// Ghost paste preview (dashed outline)
import {
  enableSegmentPastePreview as enableGhostPreview,
  disableSegmentPastePreview as disableGhostPreview,
  updateSegmentPastePreview as updateGhostPreview,
  hideSegmentPasteGhost,
} from "./SegmentPastePreview";

// -------------------------------------
// Helpers
// -------------------------------------

function getPointsCenter(points) {
  const center = new THREE.Vector3();
  points.forEach((p) => center.add(p));
  if (points.length > 0) center.divideScalar(points.length);
  return center;
}

function computePlaneNormal(points) {
  if (!points || points.length < 3) return new THREE.Vector3(0, 0, 1);

  const a = points[0];
  const b = points[1];
  const c = points[2];

  const ab = b.clone().sub(a);
  const ac = c.clone().sub(a);

  const n = new THREE.Vector3().crossVectors(ab, ac);
  if (n.lengthSq() < 1e-6) return new THREE.Vector3(0, 0, 1);

  return n.normalize();
}

/**
 * Compute a stable in-plane tangent for the original segment.
 * Prefer the edge from vertex[0] to vertex[1], projected into the plane.
 */
function computePlaneTangent(planeNormal, vertices) {
  const n0 = planeNormal.clone().normalize();
  let tangent = null;

  if (vertices && vertices.length >= 2) {
    const base = vertices[0];
    for (let i = 1; i < vertices.length; i++) {
      const candidate = vertices[i].clone().sub(base);
      if (candidate.lengthSq() < 1e-6) continue;

      const proj = candidate.sub(n0.clone().multiplyScalar(candidate.dot(n0)));
      if (proj.lengthSq() > 1e-6) {
        tangent = proj.normalize();
        break;
      }
    }
  }

  if (!tangent) {
    // Fallback: pick any world axis not parallel to normal
    tangent = new THREE.Vector3(1, 0, 0);
    if (Math.abs(tangent.dot(n0)) > 0.9) tangent.set(0, 1, 0);
    tangent.sub(n0.clone().multiplyScalar(tangent.dot(n0))).normalize();
  }

  return tangent;
}

/**
 * Build a new frame on the target plane:
 *   - n1 = target normal
 *   - u1 = projection of original tangent u0 into target plane
 *   - v1 = n1 × u1
 */
function buildTargetFrame(fromTangent, targetNormal) {
  const n1 = targetNormal.clone().normalize();
  let u1 = fromTangent.clone();

  // Project original tangent into target plane
  u1.sub(n1.clone().multiplyScalar(u1.dot(n1)));

  if (u1.lengthSq() < 1e-8) {
    // If tangent is almost parallel to normal, fall back to world axis
    u1.set(1, 0, 0);
    if (Math.abs(u1.dot(n1)) > 0.9) u1.set(0, 1, 0);
    u1.sub(n1.clone().multiplyScalar(u1.dot(n1)));
  }

  u1.normalize();
  const v1 = new THREE.Vector3().crossVectors(n1, u1).normalize();

  return { u1, v1, n1 };
}

/**
 * Rebuild world positions from stored UV coordinates in a new frame.
 *
 * copiedData:
 *   - uv: [{u, v}, ...]
 *   - center: original center (not used in reconstruction)
 *   - normal: original normal n0
 *   - tangent: original tangent u0
 *   - bitangent: original bitangent v0
 */
function rebuildPointsFromUV(copiedData, targetNormal, pasteCenter) {
  const { uv, tangent } = copiedData;
  const { u1, v1, n1 } = buildTargetFrame(tangent, targetNormal);

  const base = pasteCenter.clone();
  const points = uv.map(({ u, v }) =>
    base.clone().add(
      u1.clone().multiplyScalar(u)
        .add(v1.clone().multiplyScalar(v))
    )
  );

  return { points, normal: n1 };
}

// -------------------------------------
// Paste preview state
// -------------------------------------

let pastePreviewActive = false;
const PREVIEW_IDLE_DELAY = 500; // ms
let previewIdleTimeout = null;

/**
 * Start showing paste preview.
 */
export function startSegmentPastePreview() {
  const data = VARS.copiedSegment;
  if (!data || !data.uv || !data.uv.length) return;

  pastePreviewActive = true;

  if (LINES.referenceLine) {
    LINES.referenceLine.visible = false;
  }
  hideSmartGuides();

  enableGhostPreview();
}

/**
 * Stop the preview (hide guides + ref line + ghost).
 */
export function stopSegmentPastePreview() {
  pastePreviewActive = false;
  hideSmartGuides();

  if (previewIdleTimeout) {
    clearTimeout(previewIdleTimeout);
    previewIdleTimeout = null;
  }

  if (LINES.referenceLine) {
    LINES.referenceLine.visible = false;
    if (
      LINES.referenceLine.geometry &&
      LINES.referenceLine.geometry.attributes.position
    ) {
      LINES.referenceLine.geometry.attributes.position.needsUpdate = true;
    }
  }

  disableGhostPreview();
}

/**
 * Called when mouse is idle; just forwards to ghost preview logic.
 */
export function updateSegmentPastePreview() {
  if (!pastePreviewActive) {
    updateGhostPreview();
    return;
  }

  updateGhostPreview();
}

// Debounced mousemove → driven preview
if (typeof window !== "undefined") {
  window.addEventListener("mousemove", () => {
    if (previewIdleTimeout) {
      // ✅ fixed name: was `previewTimeout` before
      clearTimeout(previewIdleTimeout);
      previewIdleTimeout = null;
    }

    if (pastePreviewActive) {
      if (LINES.referenceLine) {
        LINES.referenceLine.visible = false;
      }
      hideSmartGuides();
      hideSegmentPasteGhost();
    }

    previewIdleTimeout = setTimeout(() => {
      updateSegmentPastePreview();
    }, PREVIEW_IDLE_DELAY);
  });
}

// -------------------------------------
// COPY
// -------------------------------------

/**
 * Copy currently selected segment into a simple clipboard object.
 *
 * We store:
 *  - uv: local 2D coordinates in the original plane frame (u0, v0)
 *  - center: original center
 *  - normal: original plane normal (n0)
 *  - tangent: original plane tangent (u0)
 *  - bitangent: original plane bitangent (v0)
 *  - details: (pitch, area, azimuth)
 */
export function copySelectedSegment(selectedIndices) {
  const { segmentIndex } = selectedIndices;
  if (segmentIndex === null) return;

  const seg = POLYGONS[segmentIndex];
  if (!seg) return;

  const vertexMeshes = VERTICES[segmentIndex] || [];
  if (!vertexMeshes.length) return;

  // World-space vertices
  const vertices = vertexMeshes.map((vm) => {
    const wp = new THREE.Vector3();
    vm.getWorldPosition(wp);
    return wp;
  });

  const center = getPointsCenter(vertices);
  const normal = computePlaneNormal(vertices);
  const tangent = computePlaneTangent(normal, vertices);
  const bitangent = new THREE.Vector3().crossVectors(normal, tangent).normalize();

  // Store local 2D coordinates for each vertex in this frame
  const uv = vertices.map((p) => {
    const d = p.clone().sub(center);
    return {
      u: d.dot(tangent),
      v: d.dot(bitangent),
    };
  });

  VARS.copiedSegment = {
    segmentIndex,
    uv,
    center,
    normal,
    tangent,
    bitangent,
    details: seg.details ? { ...seg.details } : null,
  };

  console.log("📋 Copied segment (UV-based):", {
    idx: segmentIndex,
    center,
    normal,
    tangent,
    bitangent,
    vertexCount: uv.length,
  });

  startSegmentPastePreview();
}

// -------------------------------------
// PASTE
// -------------------------------------

/**
 * Paste a copied segment at VARS.mouseNDC.
 *
 * Guarantees:
 *  - vertex order is preserved
 *  - shape & internal angles are identical
 *  - orientation around the plane is consistent (no mirroring)
 *  - plane normal matches the target roof
 */
export function pasteCopiedSegment(
  setPolygons,
  setSelectedIndices,
  setPolygonDetails
) {
  const data = VARS.copiedSegment;
  if (!data || !data.uv || !data.uv.length) return;

  stopSegmentPastePreview();

  const scene = sceneManager.scene;

  // 1️⃣ Determine where to paste
  const ndc = VARS.mouseNDC || { x: 0, y: 0 };
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera({ x: ndc.x, y: ndc.y }, sceneManager.camera);

  const meshes = VARS.Meshes || [];
  let pasteCenter = null;
  let targetNormal = null;

  if (meshes.length) {
    const hits = raycaster.intersectObjects(meshes, true);
    if (hits.length) {
      const hit = hits[0];
      pasteCenter = hit.point.clone();

      if (hit.face) {
        targetNormal = hit.face.normal
          .clone()
          .transformDirection(hit.object.matrixWorld)
          .normalize();
      }
    }
  }

  if (!pasteCenter) {
    pasteCenter = data.center.clone().add(new THREE.Vector3(1, 0, 0));
  }
  if (!targetNormal) {
    targetNormal = data.normal
      ? data.normal.clone().normalize()
      : new THREE.Vector3(0, 0, 1);
  }

  // 2️⃣ Rebuild points from stored UVs in the new frame
  const { points: rebuiltPoints, normal: finalNormal } = rebuildPointsFromUV(
    data,
    targetNormal,
    pasteCenter
  );

  // 3️⃣ Drop the whole polygon rigidly along finalNormal
  const rigidCenter = getPointsCenter(rebuiltPoints);
  let finalPoints = rebuiltPoints;

  if (meshes.length) {
    const dropRay = new THREE.Raycaster();
    const dropOrigin = rigidCenter.clone().add(finalNormal.clone().multiplyScalar(2));
    dropRay.set(dropOrigin, finalNormal.clone().negate());

    const dropHits = dropRay.intersectObjects(meshes, true);
    if (dropHits.length) {
      const hitPoint = dropHits[0].point.clone();
      const delta = hitPoint.clone().sub(rigidCenter);

      finalPoints = rebuiltPoints.map((p) => p.clone().add(delta));
    }
  }

  // snapshot for redo
  const finalPointsSnapshot = finalPoints.map(p => p.clone());

  // 4️⃣ Build polygon mesh from finalPoints (same flow as SegmentManager.addShape)
  const polygonMesh = earClipping(finalPoints);
  polygonMesh.renderOrder = 1;

  DrawingGlobals.verticesMesh = [];

  for (let p of finalPoints) {
    const vertex = createVertex(p);
    DrawingGlobals.verticesMesh.push(vertex);
    polygonMesh.add(vertex);
  }

  const { edgeData, edgeWireframe, setBackData } = createEdges(finalPoints, scene);

  const setBacks = {
    isSet: false,
    edgesData: setBackData,
    avaArea: 0,
  };

  const polygonCenter = getGeoCenter(polygonMesh);
  const segmentLabel = createNameLabel(polygonCenter);

  polygonMesh.add(edgeWireframe);
  scene.add(polygonMesh);
  scene.add(segmentLabel);

  const res = Details.calculatePitch(edgeData);
  const pitch = res.pitch_angle;
  const area = Details.getPolyArea(edgeData).toFixed(2);
  const azimu = Details.getAzimuth(res.p2, res.p1).toFixed(2);
  const details = { pitch, area, azimu };
  setBacks.avaArea = area;

  const segmentData = {
    shape: polygonMesh,
    edges: edgeData,
    label: segmentLabel,
    obstructions: [],
    panels: [],
    details,
    setBacks,
  };

  POLYGONS.push(segmentData);
  VERTICES.push([...DrawingGlobals.verticesMesh]);
  DrawingGlobals.verticesMesh = [];

  const newSegmentIndex = POLYGONS.length - 1;

  console.log("📎 Pasted segment at index", newSegmentIndex, {
    center: polygonCenter,
    vertexCount: finalPoints.length,
  });

  setPolygons((prev) => {
    if (!prev) return prev;

    const uiData = {
      label: segmentLabel.element.innerText || indexToLabel(newSegmentIndex),
      details,
      setbacks: setBacks.isSet,
      obstructions: [],
      panels: [],
    };

    return [...prev, uiData];
  });

  selectSegment(
    { segmentIndex: null, obstacleIndex: null, panelIndex: null },
    newSegmentIndex,
    setSelectedIndices,
    setPolygonDetails
  );

  if (typeof setPolygonDetails === "function") {
    setPolygonDetails(details);
  }

  // 🔴 Undo/Redo for paste
  pushUndoAction({
    label: "Paste segment",
    undo: () => {
      // if segment is already gone (e.g. deleted separately), do nothing
      if (!POLYGONS[newSegmentIndex]) return;

      // remove drag controls (in case this segment is selected)
      removeSegmentDragControls();

      // remove the actual geometry / data
      deleteSingleSegment(newSegmentIndex);

      // sync React-side list
      if (typeof setPolygons === "function") {
        setPolygons((prev) => {
          if (!prev) return prev;
          return prev.filter((_, idx) => idx !== newSegmentIndex);
        });
      }

      // fix selection indices
      if (typeof setSelectedIndices === "function") {
        setSelectedIndices((prev) => {
          if (!prev || prev.segmentIndex === null) return prev;

          if (prev.segmentIndex === newSegmentIndex) {
            // cleared selection (we just removed this segment)
            return {
              segmentIndex: null,
              obstacleIndex: null,
              panelIndex: null,
            };
          }

          if (prev.segmentIndex > newSegmentIndex) {
            // shift down indices after the removed one
            return {
              ...prev,
              segmentIndex: prev.segmentIndex - 1,
            };
          }

          return prev;
        });
      }
    },
    redo: () => {
      // don't double-create
      if (POLYGONS[newSegmentIndex]) return;

      const sceneR = sceneManager.scene;
      const pointsR = finalPointsSnapshot.map(p => p.clone());

      const polygonMeshR = earClipping(pointsR);
      polygonMeshR.renderOrder = 1;

      const vertexMeshesR = [];
      for (let p of pointsR) {
        const vertex = createVertex(p);
        vertexMeshesR.push(vertex);
        polygonMeshR.add(vertex);
      }

      const { edgeData: edgeDataR, edgeWireframe: edgeWireframeR, setBackData: setBackDataR } =
        createEdges(pointsR, sceneR);

      const setBacksR = {
        isSet: false,
        edgesData: setBackDataR,
        avaArea: 0,
      };

      const polygonCenterR = getGeoCenter(polygonMeshR);
      const segmentLabelR = createNameLabel(polygonCenterR);

      polygonMeshR.add(edgeWireframeR);
      sceneR.add(polygonMeshR);
      sceneR.add(segmentLabelR);

      const resR = Details.calculatePitch(edgeDataR);
      const pitchR = resR.pitch_angle;
      const areaR = Details.getPolyArea(edgeDataR).toFixed(2);
      const azimuR = Details.getAzimuth(resR.p2, resR.p1).toFixed(2);
      const detailsR = { pitch: pitchR, area: areaR, azimu: azimuR };
      setBacksR.avaArea = areaR;

      const segmentDataR = {
        shape: polygonMeshR,
        edges: edgeDataR,
        label: segmentLabelR,
        obstructions: [],
        panels: [],
        details: detailsR,
        setBacks: setBacksR,
      };

      // insert back at original index
      POLYGONS.splice(newSegmentIndex, 0, segmentDataR);
      VERTICES.splice(newSegmentIndex, 0, vertexMeshesR);

      // UI list insertion at same index
      if (typeof setPolygons === "function") {
        setPolygons((prev) => {
          if (!prev) return prev;
          const uiDataR = {
            label: segmentLabelR.element.innerText || indexToLabel(newSegmentIndex),
            details: detailsR,
            setbacks: setBacksR.isSet,
            obstructions: [],
            panels: [],
          };
          const arr = [...prev];
          arr.splice(newSegmentIndex, 0, uiDataR);
          return arr;
        });
      }

      // reselect this segment
      selectSegment(
        { segmentIndex: null, obstacleIndex: null, panelIndex: null },
        newSegmentIndex,
        setSelectedIndices,
        setPolygonDetails
      );

      if (typeof setPolygonDetails === "function") {
        setPolygonDetails(detailsR);
      }
    }
  });
}
